import React, { forwardRef, useState, useEffect } from "react";
import { format, parse, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";
import "../../styles/input2.css";
import { FaRegCalendarAlt } from "react-icons/fa";

interface InputBaseProps {
  tipo?: 'day' | 'month' | 'year';
  value?: Date | null;
  onFocus?: () => void;
  onChange?: (date: Date | null) => void;
  disabled?: boolean;
  inputFormat?: string;
  locale?: string;
  size?: "sm" | "md" | "lg";
  withTime?: boolean;
  onCalendarToggle?: () => void;
  onClear?: () => void;
  inputtable?: boolean;
}

const InputBase = forwardRef<HTMLInputElement, InputBaseProps>(({
  tipo = 'day',
  value,
  onFocus,
  onChange,
  disabled = false,
  inputFormat = "dd/MM/yyyy",
  locale = "pt-BR",
  size = "md",
  withTime = false,
  onCalendarToggle,
  onClear,
  inputtable = true,
}, ref) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [isInvalid, setIsInvalid] = useState<boolean>(false);

  useEffect(() => {
    setInputValue(formatValue());
    setIsInvalid(false);
  }, [value]);

  const formatValue = () => {
    if (!value) return "";
    try {
      const formatMap = {
        year: 'yyyy',
        month: 'MMMM/yyyy',
        day: 'dd/MM/yyyy'
      };
      return format(value, formatMap[tipo], { locale: ptBR });
    } catch {
      return "";
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!inputtable) return;

    const rawValue = e.target.value;
    setInputValue(rawValue);

    if (tipo === 'year' && /^\d{4}$/.test(rawValue)) {
      const newDate = new Date(Number(rawValue), 0, 1);
      onChange?.(newDate);
    } else if (tipo === 'month' && /^\d{2}-\d{4}$/.test(rawValue)) {
      const [month, year] = rawValue.split('-');
      const newDate = new Date(Number(year), Number(month) - 1, 1);
      onChange?.(newDate);
    } else if (tipo === 'day') {
      const parsedDate = parse(rawValue, inputFormat.replace("YYYY", "yyyy").replace("DD", "dd"), new Date(), { locale: ptBR });
      if (isValid(parsedDate)) {
        setIsInvalid(false);
        onChange?.(parsedDate);
      } else {
        setIsInvalid(true);
      }
    }
  };

  const handleIconClick = () => {
    if (value) {
      onClear?.();
    } else {
      onCalendarToggle?.();
    }
  };

  const generatePlaceholder = () => {
    return inputFormat
      .replace("dd", "DD")
      .replace("MM", "MM")
      .replace("yyyy", "AAAA")
      .replace("DD", "DD")
      .replace("YYYY", "AAAA");
  };

  return (
    <div className="input-wrapper">
      <input
        ref={ref}
        type="text"
        value={inputValue}
        // placeholder={generatePlaceholder()}
        onFocus={onFocus}
        onChange={handleInputChange}
        disabled={disabled}
        inputMode="numeric"
        className={`custom-input ${size} ${isInvalid ? "invalid" : ""}`}
        style={{
          maxWidth: '180px',
          boxShadow: '0px 2px 10px 0px rgba(0, 0, 0, 0.2)',
          border: 'none',
          borderRadius: '3px',
          height: '40px',
          padding: '0 10px',
          fontSize: '16px',
          fontFamily: 'Arial,Helvetica, sans-serif',
          color: '#a1a1a1',
        }}
        readOnly={!inputtable}
      />
      <span
        className={`input-icon ${value ? "clear-button" : "calendar-button"}`}
        onClick={handleIconClick}
      >
        {value ? "X" : <FaRegCalendarAlt />}
      </span>
    </div>
  );
});

InputBase.displayName = "InputBase";

export default InputBase;
